import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { MzicButtonComponent } from '@mzic/mzic-arpeggio-ui';
import { MzicSvgComponent, MzicToastService } from '@mzic/mzic-components';
import { LanguageService, PosthogAnalyticsService } from '@mzic/mzic-services';
import { FooterComponent } from '../../components/footer/footer.component';
import { HeaderComponent } from '../../components/header/header.component';

@Component({
  selector: 'mzic-contact',
  imports: [
    CommonModule,
    NgOptimizedImage,
    TranslocoModule,
    HeaderComponent,
    FooterComponent,
    MzicButtonComponent,
    MzicSvgComponent,
  ],
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.scss',
  providers: [
    MzicToastService,
    provideTranslocoScope({
      scope: 'contact',
      alias: 'lang',
    }),
  ],
})
export class ContactComponent implements OnInit, AfterViewInit, OnDestroy {
  languageService = inject(LanguageService);
  activatedRoute = inject(ActivatedRoute);
  translocoService = inject(TranslocoService);
  router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private toastService = inject(MzicToastService);
  private analyticsService = inject(PosthogAnalyticsService);

  // Section fade animation properties
  private sectionObserver: IntersectionObserver | null = null;
  private fadeableSections: string[] = [
    'hero-section',
    'contacts-section',
    'sell-with-us-section',
    'sell-with-us-mobile',
    'partners-section',
    'partners-mobile',
  ];

  constructor() {
    effect(() => {
      const lang = this.languageService.languageState();
      const current = this.router.url.split('/').pop();

      if (lang !== '') {
        this.router.navigate([`${lang}/${current}`]);
      }
    });
  }

  ngOnInit() {
    this.setLanguageFromRoute();

    // Track page view with PostHog
    this.analyticsService.trackPageView('contact', {
      language: this.languageService.languageState(),
    });
  }

  ngAfterViewInit() {
    this.initializeSectionFadeAnimation();
  }

  ngOnDestroy() {
    // Clean up section observer
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
    }

    // Clear all analytics timers
    this.analyticsService.clearAllTimers();
  }

  setLanguageFromRoute() {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const lang = params['lang'];
        if (lang) {
          this.translocoService.setActiveLang(lang);
          this.languageService.languageState.set(lang);
        }
      });
  }

  openWhatsApp() {
    // Track WhatsApp contact click
    this.analyticsService.captureWithContext('contact_click_whatsapp', {
      contact_type: 'whatsapp',
      section: 'contacts',
      language: this.languageService.languageState(),
    });

    const url =
      'https://wa.me/5511965910334/?text=Ol%C3%A1%21%20Estou%20acessando%20atrav%C3%A9s%20do%20site%20de%20investidores%20e%20preciso%20de%20ajuda';
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  openEmail() {
    // Track support email click
    this.analyticsService.captureWithContext('contact_click_email_support', {
      contact_type: 'email_support',
      email: 'support@mzic.io',
      section: 'contacts',
      language: this.languageService.languageState(),
    });

    window.location.href = 'mailto:support@mzic.io';
  }

  openAdmEmail() {
    // Track admin email click
    this.analyticsService.captureWithContext('contact_click_email_admin', {
      contact_type: 'email_admin',
      email: 'adm@mzic.io',
      section: 'sell_with_us',
      language: this.languageService.languageState(),
    });

    window.location.href = 'mailto:adm@mzic.io';
  }

  openPartnersEmail() {
    // Track partners email click
    this.analyticsService.captureWithContext('contact_click_email_partners', {
      contact_type: 'email_partners',
      email: 'adm@mzic.io',
      section: 'partners',
      language: this.languageService.languageState(),
    });

    window.location.href = 'mailto:adm@mzic.io';
  }

  copyToClipboard(text: string) {
    // Apenas no mobile (máx 768px)
    if (window.innerWidth <= 768) {
      this.copyTextToClipboard(text);
    }
  }

  private copyTextToClipboard(text: string) {
    // Try modern Clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          // Track successful copy with Clipboard API
          this.analyticsService.captureWithContext('contact_copy_email', {
            copied_text: text,
            copy_method: 'clipboard_api',
            success: true,
            toast_shown: true,
            section: 'contacts',
            language: this.languageService.languageState(),
          });

          this.showSuccessToast();
        })
        .catch((err) => {
          console.error('Clipboard API failed, trying fallback:', err);

          // Track failed Clipboard API attempt
          this.analyticsService.captureWithContext('contact_copy_email', {
            copied_text: text,
            copy_method: 'clipboard_api',
            success: false,
            error: err.message,
            section: 'contacts',
            language: this.languageService.languageState(),
          });

          this.fallbackCopyToClipboard(text);
        });
    } else {
      // Fallback for older browsers or insecure contexts
      this.fallbackCopyToClipboard(text);
    }
  }

  private fallbackCopyToClipboard(text: string) {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Make it invisible and non-interactive
    textArea.style.position = 'fixed';
    textArea.style.top = '-9999px';
    textArea.style.left = '-9999px';
    textArea.style.opacity = '0';
    textArea.setAttribute('readonly', '');

    document.body.appendChild(textArea);

    // Select and copy
    try {
      textArea.select();
      textArea.setSelectionRange(0, text.length);

      const successful = document.execCommand('copy');
      if (successful) {
        // Track successful fallback copy
        this.analyticsService.captureWithContext('contact_copy_email', {
          copied_text: text,
          copy_method: 'fallback',
          success: true,
          toast_shown: true,
          section: 'contacts',
          language: this.languageService.languageState(),
        });

        this.showSuccessToast();
      } else {
        console.error('Fallback copy command failed');

        // Track failed fallback copy
        this.analyticsService.captureWithContext('contact_copy_email', {
          copied_text: text,
          copy_method: 'fallback',
          success: false,
          toast_shown: true,
          section: 'contacts',
          language: this.languageService.languageState(),
        });

        this.showErrorToast();
      }
    } catch (err: any) {
      console.error('Fallback copy failed:', err);

      // Track exception in fallback copy
      this.analyticsService.captureWithContext('contact_copy_email', {
        copied_text: text,
        copy_method: 'fallback',
        success: false,
        error: err.message,
        toast_shown: true,
        section: 'contacts',
        language: this.languageService.languageState(),
      });

      this.showErrorToast();
    } finally {
      document.body.removeChild(textArea);
    }
  }

  private showSuccessToast() {
    this.toastService.create({
      title: 'Copiado com sucesso',
      icon: 'assets/icons/mzic-check-mark-filled.svg',
      iconColor: 'neutral-700',
      width: '300px',
      customClass: 'success-2',
    });
  }

  private showErrorToast() {
    this.toastService.create({
      title: 'Erro ao copiar',
      icon: 'assets/icons/mzic-error.svg',
      iconColor: 'error-500',
      width: '300px',
      customClass: 'error',
    });
  }

  private initializeSectionFadeAnimation() {
    // Mathematical calculation for optimal fade thresholds
    // Desktop: More precise control, smoother transitions
    // Mobile: Faster transitions, less resource intensive
    const isMobile = window.innerWidth <= 768;
    const viewportHeight = window.innerHeight;

    // Calculate intersection thresholds mathematically
    // Desktop: More granular steps for smoother animation
    // Mobile: Fewer steps for better performance
    const thresholdSteps = isMobile ? 5 : 10;
    const thresholds = Array.from(
      { length: thresholdSteps },
      (_, i) => i / (thresholdSteps - 1),
    );

    // Mathematical root margin calculation
    // Creates a buffer zone based on viewport height
    // Desktop: Larger buffer for smoother experience
    // Mobile: Smaller buffer to avoid early triggering
    const rootMarginPercent = isMobile ? 6.75 : 4; // Percentage of viewport height
    const rootMarginPx = Math.round(viewportHeight * (rootMarginPercent / 100));

    // Create observer with calculated mathematical parameters
    this.sectionObserver = new IntersectionObserver(
      (entries) => this.handleSectionIntersection(entries),
      {
        root: null, // Use viewport as root
        rootMargin: `-${rootMarginPx}px 0px -${rootMarginPx}px 0px`, // Symmetric margins
        threshold: thresholds, // Granular thresholds for smooth animation
      },
    );

    // Initialize sections with fade-out state
    // Add observer to each fadeable section
    setTimeout(() => {
      this.fadeableSections.forEach((sectionClass) => {
        const section = document.querySelector(
          `.${sectionClass}`,
        ) as HTMLElement;
        if (section) {
          // Initialize with fade-out state
          section.classList.add('section-fade-out');
          section.classList.remove('section-fade-in');

          // Observe the section
          this.sectionObserver?.observe(section);
        }
      });
    }, 100); // Small delay to ensure DOM is ready
  }

  private handleSectionIntersection(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      const section = entry.target as HTMLElement;

      // Mathematical fade calculation based on intersection ratio
      const intersectionRatio = entry.intersectionRatio;

      // Calculate fade threshold mathematically
      const isMobile = window.innerWidth <= 768;
      const fadeInThreshold = isMobile ? 0.25 : 0.3;
      const fadeOutThreshold = isMobile ? 0.15 : 0.2;

      // Track section visibility for analytics
      const sectionClasses = Array.from(section.classList);
      const sectionName =
        sectionClasses
          .find((cls) => cls.endsWith('-section'))
          ?.replace('-section', '') || 'unknown';

      if (intersectionRatio >= fadeInThreshold) {
        this.analyticsService.startSectionTimer(sectionName);
      } else if (intersectionRatio <= fadeOutThreshold) {
        this.analyticsService.stopSectionTimer(sectionName);
      }

      // Apply fade based on intersection ratio
      if (intersectionRatio >= fadeInThreshold) {
        section.classList.add('section-fade-in');
        section.classList.remove('section-fade-out');
      } else if (intersectionRatio <= fadeOutThreshold) {
        section.classList.add('section-fade-out');
        section.classList.remove('section-fade-in');
      }

      // Set CSS custom property for advanced animations
      section.style.setProperty(
        '--intersection-ratio',
        intersectionRatio.toString(),
      );
    });
  }
}
